/* eslint-disable react/destructuring-assignment */
import { Icon, IconButton } from '@chakra-ui/react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import {
    Select as BaseSelect,
    ClearIndicatorProps,
    DropdownIndicatorProps,
    GroupBase,
    Props,
    chakraComponents,
} from 'chakra-react-select';
import { FaIcon } from '../fa-icon';

export interface SelectOption<T> {
    label: string;
    value: T;
}

interface SelectProps {
    placeholderColor?: string;
    optionColorScheme?: string;
    isLabelDropdown?: boolean;
    isSearchable?: boolean;
}

const components = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ClearIndicator: (props: ClearIndicatorProps<any, boolean, any>) => (
        <chakraComponents.ClearIndicator {...props}>
            <IconButton
                colorScheme="blackAlpha"
                size="xs"
                pointerEvents="all"
                variant="ghost"
                aria-label="clear selection"
                icon={<FaIcon icon={faTimes} />}
            />
        </chakraComponents.ClearIndicator>
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    DropdownIndicator: (props: DropdownIndicatorProps<any, boolean, any>) => (
        <chakraComponents.DropdownIndicator {...props}>
            <Icon fontSize={props.selectProps.size}>
                <path fill="currentColor" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
            </Icon>
        </chakraComponents.DropdownIndicator>
    ),
};

export function Select<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>({
    isLabelDropdown = false,
    isSearchable = true,
    placeholderColor = 'gray.500',
    optionColorScheme = 'gray',
    colorScheme,
    ...selectProps
}: Props<Option, IsMulti, Group> & SelectProps) {
    return (
        <BaseSelect
            {...selectProps}
            useBasicStyles
            colorScheme={colorScheme}
            variant={isLabelDropdown ? 'unstyled' : 'outline'}
            isSearchable={isSearchable}
            chakraStyles={{
                menu: (provided) => ({
                    ...provided,
                    zIndex: 3,
                    boxShadow: 'sm',
                    borderRadius: 'md',
                    color: 'initial',
                    fontWeight: 'initial',
                }),
                option: (provided, state) => ({
                    ...provided,
                    _selected: {
                        bgColor: `${optionColorScheme}.300`,
                    },
                    _hover: {
                        bgColor: !state.isSelected ? `${optionColorScheme}.100` : undefined,
                    },
                }),
                control: (provided) => ({
                    ...provided,
                    ...(isLabelDropdown && {
                        bgColor: 'transparent',
                        fontSize: 'md',
                    }),
                }),
                menuList: (provided) => ({
                    ...provided,
                    borderRadius: 'md',
                    boxShadow: 'unset',
                }),
                multiValue: (provided) => ({
                    ...provided,
                    bgColor: 'gray.100',
                    color: 'black',
                }),
                clearIndicator: (provided) => ({
                    ...provided,
                    mr: 0,
                    _hover: {
                        bgColor: 'transparent',
                    },
                }),
                dropdownIndicator: (provided) => ({
                    ...provided,
                    ml: 0,
                    width: 'unset',
                    mr: 3,
                    paddingInlineEnd: 0,
                    color: 'black',
                }),
                placeholder: (provided) => ({
                    ...provided,
                    color: placeholderColor,
                }),
                loadingIndicator: (provided) => ({
                    ...provided,
                    color: 'gray.300',
                }),
            }}
            components={components}
        />
    );
}
